import { useMemo, useState } from "react"
import { Check, Clipboard, Clock, Globe2, LoaderCircle, ShieldCheck, Trash2 } from "lucide-react"
import { toast } from "sonner"
import { Button } from "@/components/ui/button"
import { AddDomainForm } from "@/components/editor/add-domain-form"
import { editorCardClass, editorDescriptionClass, editorHeadingClass } from "@/components/editor/editor-styles"
import { CodeBlock } from "@/components/shared/code-block"
import { StatusBadge } from "@/components/shared/status-badge"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { api } from "@/lib/api"
import { Switch } from "@/components/ui/switch"
import { normalizeDomain } from "@/lib/domain"
import type { ChatbotDetail, ChatbotSettings } from "@/lib/types"

const APPEARANCE_DEFAULTS: Partial<ChatbotSettings> = {
  assistantName: "Assistente", chatTitle: "Atendimento", chatSubtitle: "Online",
  welcomeMessage: "Olá! Como posso ajudar você hoje?", primaryColor: "#EE7100", launcherColor: "#EE7100", launcherColorDark: "#EE7100",
  headerColor: "#FFFFFF", userMessageColor: "#EE7100", assistantMessageColor: "#F1F1F1", textColor: "#252525",
  chatBackground: "#FFFFFF", position: "bottom-right", avatar: "sparkles", avatarUrl: "/images/mil-play-assistant-avatar.png",
  launcherIcon: "message", launcherStyle: "solid", launcherGradientStart: "#EE7100", launcherGradientEnd: "#EE7100", launcherGradientAngle: 135, launcherGradientStartDark: "#EE7100", launcherGradientEndDark: "#EE7100", launcherSize: 60, launcherRadius: 999, headerGradientEnabled: false, headerGradientStart: "#FFFFFF", headerGradientEnd: "#FFFFFF", headerGradientStartDark: "#FFFFFF", headerGradientEndDark: "#FFFFFF", headerGradientAngle: 135, chatBackgroundGradientEnabled: false, chatBackgroundGradientStart: "#FFFFFF", chatBackgroundGradientEnd: "#FFFFFF", chatBackgroundGradientStartDark: "#FFFFFF", chatBackgroundGradientEndDark: "#FFFFFF", chatBackgroundGradientAngle: 135, motionStyle: "smooth",
  widgetRadius: 30, widgetSize: "standard", showBranding: true, placeholder: "Faça sua pergunta...", messageFontSize: 14,
  calloutTitle: "Precisa de ajuda?", calloutSubtitle: "", calloutEnabled: false, calloutDelaySeconds: 0, calloutColor: "", calloutColorDark: "",
}
function isAppearanceCustomized(settings: ChatbotSettings) {
  return (Object.keys(APPEARANCE_DEFAULTS) as (keyof ChatbotSettings)[]).some((key) => settings[key] !== APPEARANCE_DEFAULTS[key])
}

function normalizeHostname(input: string) {
  return normalizeDomain(input).hostname
}

export function buildInstallScript(publicKey: string) {
  return `<!-- BubbleDesk AI assistant -->\n<script\n  src="${window.location.origin}/widget.js"\n  data-chatbot="${publicKey}"\n  data-position="bottom-right"\n  defer\n></script>`
}

export function openStandaloneTest(chatbotId: string) {
  window.open("/chatbots/" + encodeURIComponent(chatbotId) + "/test", "_blank", "noopener,noreferrer")
}

export function InstallationSection({ detail, onRefresh }: { detail: ChatbotDetail; onRefresh: (detail: ChatbotDetail) => void }) {
  const [domainInput, setDomainInput] = useState("")
  const [domainError, setDomainError] = useState<string>()
  const [savingDomain, setSavingDomain] = useState(false)
  const [removing, setRemoving] = useState<string>()
  const [updatingStatus, setUpdatingStatus] = useState<string>()
  const [copied, setCopied] = useState(false)
  const [installationView, setInstallationView] = useState("code")
  const domains = detail.installDomains.map(({ hostname }) => hostname)
  const ready = detail.status === "active" && detail.installDomains.some((domain) => domain.status !== "paused")
  const script = useMemo(() => buildInstallScript(detail.publicKey), [detail.publicKey])
  const checklist = [
    { done: isAppearanceCustomized(detail), label: "Aparência configurada", description: "Widget usa o tema salvo" },
    { done: detail.stats.domains > 0, label: "Conteúdo conectado", description: "Ao menos uma fonte de pesquisa" },
    { done: detail.installDomains.some((domain) => domain.status !== "paused"), label: "Domínio de instalação ativo", description: "Ao menos um domínio disponível" },
    { done: detail.status === "active", label: "Chatbot ativo", description: "Disponível para novas conversas" },
  ]

  async function addDomain() {
    let hostname: string
    try { hostname = normalizeHostname(domainInput); setDomainError(undefined) }
    catch (error) { return setDomainError(error instanceof Error ? error.message : "Domínio inválido.") }
    if (domains.includes(hostname)) return setDomainError("Este domínio já está autorizado.")
    setSavingDomain(true)
    try { onRefresh(await api.updateInstallDomains(detail.id, [...domains, hostname])); setDomainInput(""); toast.success(`${hostname} autorizado para instalação.`) }
    catch (error) { setDomainError(error instanceof Error ? error.message : "Não foi possível autorizar o domínio.") }
    finally { setSavingDomain(false) }
  }

  async function removeDomain(hostname: string) {
    setRemoving(hostname)
    try { onRefresh(await api.updateInstallDomains(detail.id, domains.filter((item) => item !== hostname))); toast.success("Domínio de instalação removido.") }
    catch (error) { toast.error(error instanceof Error ? error.message : "Não foi possível remover o domínio.") }
    finally { setRemoving(undefined) }
  }

  async function toggleDomain(domainId: string, hostname: string, active: boolean) {
    setUpdatingStatus(domainId)
    try {
      onRefresh(await api.updateInstallDomainStatus(detail.id, domainId, active ? "active" : "paused"))
      toast.success(active ? hostname + " reativado." + (detail.status === "paused" ? " A pausa global continua ativa." : "") : "Assistente pausado em " + hostname + ".")
    } catch (error) { toast.error(error instanceof Error ? error.message : "Não foi possível atualizar a disponibilidade.") }
    finally { setUpdatingStatus(undefined) }
  }

  async function copyScript() {
    try {
      await navigator.clipboard.writeText(script)
      setCopied(true); toast.success("Código de instalação copiado.")
      window.setTimeout(() => setCopied(false), 2000)
    } catch { toast.error("Não foi possível copiar automaticamente. Selecione o código e copie manualmente.") }
  }

  return (
    <div className="space-y-5">
      <Tabs value={installationView} onValueChange={setInstallationView} className="space-y-4">
        <TabsList aria-label="Seções da instalação" className="grid h-auto w-full grid-cols-2 gap-1 rounded-xl border border-border bg-card p-1 sm:grid-cols-4">
          <TabsTrigger value="code" className="h-9 rounded-lg px-2 text-[11px] font-semibold shadow-none data-[state=active]:bg-accent data-[state=active]:text-accent-foreground">Código</TabsTrigger>
          <TabsTrigger value="steps" className="h-9 rounded-lg px-2 text-[11px] font-semibold shadow-none data-[state=active]:bg-accent data-[state=active]:text-accent-foreground">Passo a passo</TabsTrigger>
          <TabsTrigger value="domains" className="h-9 rounded-lg px-2 text-[11px] font-semibold shadow-none data-[state=active]:bg-accent data-[state=active]:text-accent-foreground">Domínios</TabsTrigger>
          <TabsTrigger value="checklist" className="h-9 rounded-lg px-2 text-[11px] font-semibold shadow-none data-[state=active]:bg-accent data-[state=active]:text-accent-foreground">Checklist</TabsTrigger>
        </TabsList>

        <TabsContent value="code" className="mt-0">
          <section className={editorCardClass}>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0 flex-1">
            <h2 className={editorHeadingClass}>Código de instalação</h2>
            <p className={editorDescriptionClass}>Cole este trecho antes da tag de fechamento <code className="rounded bg-muted px-1 py-0.5 font-mono text-[11px]">&lt;/body&gt;</code> do seu site.</p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <Button type="button" variant="outline" className="field-bg h-10 shrink-0 rounded-xl border-border px-3 text-xs font-semibold" onClick={() => void copyScript()}>
              {copied ? <Check className="mr-1.5 size-3.5 text-success" /> : <Clipboard className="mr-1.5 size-3.5" />}
              {copied ? "Copiado" : "Copiar código"}
            </Button>
          </div>
        </div>
        <div className="mt-4">
          <CodeBlock code={script} language="index.html" onCopy={() => void copyScript()} copied={copied} showCopy={false} />
        </div>
        <p className="mt-2.5 flex items-center gap-1.5 text-[11px] muted">
          <ShieldCheck className="size-3.5" />Sua chave pública é exclusiva deste assistente.
        </p>
          </section>
        </TabsContent>

        <TabsContent value="steps" className="mt-0">
          <section className={editorCardClass}>
        <div className="mb-4">
          <h2 className={editorHeadingClass}>Instruções de instalação</h2>
          <p className={editorDescriptionClass}>Coloque seu assistente no ar em poucos passos simples.</p>
        </div>
        <ol className="space-y-5">
          {[
            ["1", "Copie o código de instalação", "Use o botão acima para copiar o trecho completo do widget."],
            ["2", "Abra o HTML do seu site", "Abra o código-fonte do site que você autorizou abaixo."],
            ["3", "Cole antes da tag de fechamento do body", "Coloque o trecho logo antes de </body> em todas as páginas onde o widget deve aparecer."],
            ["4", "Publique e verifique seu site", "Publique as alterações e volte aqui para confirmar que o widget está disponível."]
          ].map(([step, title, description]) => (
            <li key={step} className="flex gap-4">
              <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-muted text-[12px] font-bold">{step}</span>
              <div>
                <h3 className="text-[13px] font-semibold text-foreground">{title}</h3>
                <p className="mt-1 text-[12px] muted leading-5">{description}</p>
              </div>
            </li>
          ))}
        </ol>
          </section>
        </TabsContent>

        <TabsContent value="domains" className="mt-0">
          <section className={editorCardClass}>
        <div className="mb-4">
          <h2 className={editorHeadingClass}>Domínios autorizados</h2>
          <p className={editorDescriptionClass}>Ative ou pause o assistente em cada domínio. A pausa global prevalece nos sites; a área de teste continua disponível.</p>
        </div>
        <AddDomainForm id="install-domain" value={domainInput} onValueChange={(next) => { setDomainInput(next); setDomainError(undefined) }} onSubmit={addDomain} submitting={savingDomain || Boolean(removing || updatingStatus)} error={domainError} placeholder="exemplo.com" submitLabel="Adicionar domínio" />
        <div className="mt-4 divide-y divide-border border-t border-border">
          {domains.length ? detail.installDomains.map(({ id, hostname: domain, status }) => (
            <div key={domain} className="flex flex-col gap-2 py-3 sm:flex-row sm:items-center sm:justify-between text-xs">
              <div className="flex items-center gap-2.5">
                <span className="flex size-7 items-center justify-center rounded-lg bg-success/10 text-success">
                  <Globe2 className="size-3.5" />
                </span>
                <div>
                  <p className="font-semibold text-foreground">{domain}</p>
                  <p className="text-[10px] muted">{status === "paused" ? "Pausado neste domínio" : detail.status === "paused" ? "Publicação bloqueada pela pausa global" : "Disponível neste domínio"}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <StatusBadge tone={status === "paused" || detail.status === "paused" ? "warning" : "success"}>{status === "paused" ? "Pausado" : detail.status === "paused" ? "Pausa global" : "Ativo"}</StatusBadge>
                <Switch checked={status !== "paused"} disabled={Boolean(updatingStatus || removing || savingDomain)} aria-label={"Ativar assistente em " + domain} onCheckedChange={(active) => void toggleDomain(id, domain, active)} />
                <Button variant="ghost" size="icon" className="size-7 border border-border muted hover:border-destructive/40 hover:text-destructive" disabled={Boolean(removing || updatingStatus || savingDomain)} onClick={() => void removeDomain(domain)}>
                  {removing === domain ? <LoaderCircle className="size-3 animate-spin" /> : <><Trash2 className="size-3" /><span className="sr-only">Remover {domain}</span></>}
                </Button>
              </div>
            </div>
          )) : (
            <div className="py-4 text-xs muted">Nenhum domínio autorizado ainda.</div>
          )}
        </div>
          </section>
        </TabsContent>

        <TabsContent value="checklist" className="mt-0">
          <section className={editorCardClass}>
        <div className="flex flex-col justify-between gap-3 sm:flex-row sm:items-start mb-4">
          <div>
            <h2 className={editorHeadingClass}>Pronto para publicar?</h2>
            <p className={editorDescriptionClass}>{ready ? "Tudo está configurado para o seu assistente." : "Complete a lista antes de publicar."}</p>
          </div>
          <span className={`flex w-fit items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[10px] font-semibold ${ready ? "border-success/20 bg-success/10 text-success" : "border-warning/20 bg-warning/10 text-warning"}`}>
            <span className={`size-1.5 rounded-full ${ready ? "bg-success" : "bg-warning"}`} />
            {ready ? "Pronto para publicar" : "Não está pronto"}
          </span>
        </div>
        <div className="grid gap-2.5 sm:grid-cols-2">
          {checklist.map(({ done, label }) => (
            <div key={label} className="flex items-center gap-3 rounded-xl border border-border bg-muted/20 p-3">
              <span className={`flex size-7 items-center justify-center rounded-full ${done ? "bg-success/10 text-success" : "bg-muted muted"}`}>
                {done ? <Check className="size-3.5" /> : <span className="size-1.5 rounded-full bg-current" />}
              </span>
              <span className={`text-[12px] font-medium ${done ? "text-foreground" : "muted"}`}>{label}</span>
            </div>
          ))}
        </div>
        <div className="mt-4 flex items-center border-t border-border pt-3 text-xs muted">
          <span className="flex items-center gap-1.5"><Clock className="size-3.5" />Tempo estimado para colocar no ar: menos de 5 minutos</span>
        </div>
          </section>
        </TabsContent>
      </Tabs>
    </div>
  )
}
